/**
 * Score Circle Component (Shared)
 *
 * SVG-based circular progress indicator for displaying ATS scores.
 * Migrated from extension/sidepanel/src/ui/ScoreCircle.tsx to shared package.
 *
 * Features:
 * - SVG circular progress with smooth CSS transition
 * - Color variants based on score level (excellent/good/fair/low)
 * - Three size variants: sm (80px), md (120px), lg (160px)
 * - Accessible: role="progressbar" with ARIA attributes
 * - forwardRef support for composition
 */

import React, { useMemo } from 'react';
import { cx, getScoreVariant } from '../utils/score-helpers';
import type { ScoreCircleProps } from '../types';

type ScoreCircleVariant = 'excellent' | 'good' | 'fair' | 'low';

const variantColors: Record<ScoreCircleVariant, string> = {
  excellent: 'var(--ats-score-excellent)',
  good: 'var(--ats-score-good)',
  fair: 'var(--ats-score-fair)',
  low: 'var(--ats-score-low)',
};

const sizeConfig: Record<
  'sm' | 'md' | 'lg',
  { svg: number; radius: number; strokeWidth: number; fontSize: string }
> = {
  sm: { svg: 80, radius: 36, strokeWidth: 6, fontSize: 'ats-report__score-text--sm' },
  md: { svg: 120, radius: 52, strokeWidth: 8, fontSize: 'ats-report__score-text--md' },
  lg: { svg: 160, radius: 72, strokeWidth: 10, fontSize: 'ats-report__score-text--lg' },
};

/**
 * Calculate SVG stroke-dashoffset to represent a given percentage.
 */
function calculateStrokeOffset(percentage: number, radius: number): number {
  const circumference = 2 * Math.PI * radius;
  return circumference - (percentage / 100) * circumference;
}

export const ScoreCircle = React.forwardRef<HTMLDivElement, ScoreCircleProps>(
  ({ score, label, size = 'md', className }, ref) => {
    const variant = useMemo(() => getScoreVariant(score), [score]);
    const config = sizeConfig[size];
    const circumference = 2 * Math.PI * config.radius;
    const strokeOffset = useMemo(
      () => calculateStrokeOffset(score, config.radius),
      [score, config.radius],
    );

    return (
      <div
        ref={ref}
        className={cx(
          'ats-report-score-circle',
          `ats-report-score-circle--${variant}`,
          className,
        )}
        role="progressbar"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Score: ${score}%`}
      >
        <div className="ats-report-score-circle__svg-wrap">
          <svg
            className="ats-report-score-circle__svg"
            width={config.svg}
            height={config.svg}
            viewBox={`0 0 ${config.svg} ${config.svg}`}
          >
            {/* Background track */}
            <circle
              className="ats-report-score-circle__track"
              cx={config.svg / 2}
              cy={config.svg / 2}
              r={config.radius}
              strokeWidth={config.strokeWidth}
              fill="none"
            />
            {/* Progress arc */}
            <circle
              className="ats-report-score-circle__progress"
              cx={config.svg / 2}
              cy={config.svg / 2}
              r={config.radius}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeOffset}
              stroke={variantColors[variant]}
              fill="none"
              transform={`rotate(-90, ${config.svg / 2}, ${config.svg / 2})`}
            />
          </svg>
          <div className="ats-report-score-circle__value-wrap">
            <span className={cx('ats-report-score-circle__value', config.fontSize)}>
              {score}%
            </span>
          </div>
        </div>
        {label && (
          <div className="ats-report-score-circle__label">{label}</div>
        )}
      </div>
    );
  },
);

ScoreCircle.displayName = 'ScoreCircle';

export default ScoreCircle;
